import { useState } from "react";
import { twoFactor, useSession } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Fingerprint, ShieldCheck, Smartphone } from "lucide-react";

export function SecuritySettings() {
  const { data: session } = useSession();
  const [totpUri, setTotpUri] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEnable2FA = async () => {
    setLoading(true);
    try {
      const res = await twoFactor.enable({ password });
      if ((res as any)?.data?.totpURI) {
        setTotpUri((res as any).data.totpURI);
        toast.info("Scan the QR code with your authenticator app");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to enable 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setLoading(true);
    try {
      const res = await twoFactor.verifyTotp({ code: verifyCode });
      if (res?.error) throw new Error(res.error.message);
      toast.success("2FA enabled successfully");
      setTotpUri(null);
      setVerifyCode("");
    } catch (err: any) {
      toast.error(err.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPasskey = async () => {
    toast.info("Passkeys are not enabled yet for this Better Auth setup.");
  };

  if (!session?.user) return null;

  return (
    <div className="space-y-6">
      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Smartphone className="h-5 w-5" />
            Two-Factor Authentication (TOTP)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!totpUri ? (
            <>
              <Input
                type="password"
                placeholder="Confirm your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <Button onClick={handleEnable2FA} disabled={loading || !password} variant="outline">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Enable 2FA
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Scan this URI in your authenticator app (Google Authenticator, Authy, etc.):
              </p>
              <code className="block p-3 bg-muted rounded text-xs break-all">{totpUri}</code>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                />
                <Button onClick={handleVerify2FA} disabled={loading || verifyCode.length !== 6}>
                  Verify
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Passkeys / Biometric */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Fingerprint className="h-5 w-5" />
            Passkeys & Biometric Login
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Passkey support is planned, but this Better Auth setup is currently using email/password plus optional TOTP only.
          </p>
          <Button onClick={handleAddPasskey} variant="outline">
            <Fingerprint className="mr-2 h-4 w-4" />
            Passkeys Coming Soon
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
